/**
 * Trade — the shapes and the numbers. Imports nothing from the campaign.
 *
 * This file is the argument for the whole system, so it is worth stating up
 * front what was taken from the EU4 model and what was left behind.
 *
 * **The idea that was kept.** A trade node is not the interesting part. The
 * interesting part is that *value has a direction*: what a province produces
 * does not stay where it was made, it moves along a graph toward the sea, and
 * every crown whose ground it passes through gets a chance to take a cut. Take
 * the cut and you are richer this month; let it run and you may take a bigger
 * cut further down, if you own the place it ends up. That is a spatial argument
 * about geography and force, which is exactly what a strategic map is for, and
 * it is worth having even in a realm this small.
 *
 * The second idea that was kept is **scarcity**. A crown has two to six
 * merchants for sixteen nodes. Every placement is a refusal of fifteen others,
 * and a merchant on the road takes months to arrive, so the decision has a cost
 * that is felt rather than merely accounted. Without that, trade is a readout.
 *
 * **What was cut, and why.**
 *
 * - *Trade goods and their prices.* EU4 has a second economy of thirty
 *   commodities whose prices move. This game already has one — twelve resources
 *   and a market in the settlement — and a province's `resource` yield already
 *   goes straight to the treasury. A second price system would be a second
 *   thing to learn that says the same thing twice.
 * - *Trade companies, merchant republics, centres of trade, trade policies,
 *   privateering.* All of these are modifiers on top of the base loop. The base
 *   loop is what this pass owes; a modifier catalogue is content and can be
 *   added later without any of this moving.
 * - *Per-country per-node "trade power" from light ships on protect-trade.*
 *   Ships matter here — see `POWER_PER_PORT` and `SEA_LANE_WEIGHT` — but as
 *   ports and lanes, which is what this realm will actually have, rather than as
 *   a naval mission the player has to micromanage on sixteen nodes.
 * - *Home node, trade capital, moving your home node.* One node, chosen once, at
 *   a cost, that everything else is measured against. It exists in EU4 to give
 *   the steering half of the system a destination. Here the destination is
 *   "wherever you have control", which is legible from the map and needs no
 *   extra state.
 * - *Node-level "collect outside home node" penalties.* A tax on doing the
 *   obvious thing, which exists to protect the home-node rule that was cut.
 *
 * What is left is: value flows downhill, control decides who takes it, and you
 * have a handful of merchants to argue with the flow.
 */

/** What a merchant has been told to do where it stands. */
export const MerchantMission = {
  /**
   * Take the crown's cut here, and raise how much of the node's value is taken
   * at all. Money now, in a place you already hold.
   */
  Collect: 0,
  /**
   * Take nothing here. Push: *less* of this node's value stays put at all, and
   * more of what leaves goes down one particular road. Money later, somewhere
   * you hold harder. This is the "push trade" half, and it is the only reason
   * the node graph has arrows.
   *
   * The draining half matters as much as the aiming half, and the first cut of
   * this system did not have it — a steerer only nudged the split between roads,
   * which turned out to be worth about a fifth of what a collector was worth in
   * the same square, so the correct play was always to collect and the AI never
   * once steered in forty years of campaign. A steerer now works against the
   * node's retention in proportion to its control, which is what makes it a
   * weapon: it robs whoever is collecting here and sends the takings to a market
   * you hold harder.
   */
  Steer: 1,
} as const;
export type MerchantMissionId = (typeof MerchantMission)[keyof typeof MerchantMission];

export const MISSION_NAMES: Record<number, string> = {
  [MerchantMission.Collect]: 'collecting',
  [MerchantMission.Steer]: 'steering',
};

/**
 * One node of the trade graph: a market town and the country that trades
 * through it.
 *
 * Built once, from the province table, at the start of a campaign — and then
 * **state**, hashed and saved, for the same reason `CampaignState.links` is.
 * Deliberately not rebuilt when a road is cut: a trade route is a decade-scale
 * arrangement of who knows whom, and it does not re-plot itself because a bridge
 * came down. What a cut road changes is who can march there to contest it.
 */
export interface TradeNode {
  id: number;
  /** The market town's name — the node's seat province, and where the fair is. */
  name: string;
  /** Province index the node is named for and centred on. */
  seat: number;
  /** Every province that trades through here. Ascending. */
  provinces: number[];
  /**
   * Hops from salt water. 0 is a terminal: nothing flows out of it, everything
   * that reaches it is collected there, and it is therefore the richest and most
   * fought-over ground on the board.
   */
  depth: number;
  /** Nodes value flows *out* to. Ascending. Always downhill, so never a cycle. */
  outgoing: number[];
  /** How good each outgoing road is, in the same order. See `edgeWeight`. */
  outWeight: number[];
}

/** What a node did this month. Rewritten every month; hashed, because it is paid out. */
export interface TradeNodeState {
  /** Value the node's own provinces put in this month. */
  local: number;
  /** `local` plus everything that flowed in from upstream. */
  value: number;
  /** 0..1 — the share of `value` that was taken here rather than passed on. */
  retained: number;
  /**
   * Summed control of the crowns collecting here, and of those steering. Both
   * capped at 1.
   *
   * Stored rather than recomputed because three separate readers want them —
   * the flow that sets `retained`, the council deciding whether a merchant is
   * worth sending, and the panel, which has to be able to say *why* a market is
   * leaking. Recomputing them in the council would mean the AI reasoning from a
   * second implementation of the retention rule, which is exactly the kind of
   * drift that makes an opponent look like it is cheating.
   */
  collectShare: number;
  steerShare: number;
  /** Control share per nation. Sums to 1 where anybody has any, 0 where nobody does. */
  control: number[];
  /** Gold each nation actually took here this month. Per nation. */
  income: number[];
  /** What went down each outgoing road, in `TradeNode.outgoing` order. */
  outflow: number[];
}

/** One of a crown's handful. */
export interface Merchant {
  id: number;
  nation: number;
  /** Node it is posted to. */
  node: number;
  /** A `MerchantMission`. */
  mission: number;
  /** For `Steer`, the downstream node it is pushing toward. -1 when collecting. */
  toward: number;
  /**
   * Months left on the road before it is in place.
   *
   * The one piece of EU4's merchant model that is not about arithmetic, and it
   * earns its field: without it a merchant is a free radio button and the whole
   * scarcity argument collapses into "recompute the best placement every month".
   * With it, moving a merchant costs two or three months of whatever it was
   * doing, so where you put the first one is a decision you live with.
   */
  travel: number;
}

export interface TradeState {
  nodes: TradeNode[];
  /** Parallel to `nodes`. */
  states: TradeNodeState[];
  merchants: Merchant[];
  nextMerchantId: number;
}

// ── The numbers ────────────────────────────────────────────────────────────
//
// Every one of these is per month, like everything else in this layer.

/**
 * How much of a province's gold wealth is *trade* rather than tax.
 *
 * This is the load-bearing number and the only one that moves an existing
 * balance. A province used to hand its whole `wealth` to whoever owned it; now
 * this share of it goes into the node instead and comes back only through
 * control. A crown that holds the ground *and* the node it trades through is
 * very nearly where it was. A crown that holds ground whose trade runs out
 * through somebody else's port is poorer, and the crown with the port is richer
 * by exactly the same amount — the realm's total gold is conserved, and the
 * redistribution is the game.
 *
 * Set at a third rather than at EU4's roughly-a-half because this realm's crowns
 * are small and a bad month has to remain survivable. It is the first dial to
 * turn if trade feels decorative.
 */
export const TRADE_SHARE = 0.35;

/**
 * The share of a node's value that stays put when nobody is collecting.
 *
 * Not zero, and the reason is the interior. A node eight hops inland that leaked
 * everything would make the whole west of the realm worthless, and "the coast
 * wins" is not a strategy, it is a map. At 0.6 an unattended node keeps most of
 * its own and sends a real slice on, so the coast is genuinely richer without the
 * interior being a wasteland — and a merchant is what closes the gap.
 */
export const RETAIN_FLOOR = 0.6;

/**
 * How hard one steering merchant leans on the *choice of road*, as a multiple of
 * the base weight of the road it is pushing toward.
 *
 * Scaled by the steering crown's control, so a merchant in a node you barely
 * hold barely moves anything. That is deliberate and it is the whole balance of
 * the mechanic: steering is a way to spend a node you have a real say in, not a
 * way to reach into somebody else's and empty it from the outside.
 *
 * `retainFloorWith` below is the other half — how much a steerer stops staying
 * put at all — and it is the larger effect of the two.
 */
export const STEER_PULL = 2.4;

/**
 * The weight a steering merchant brings *of his own*, before any control.
 *
 * This constant is the fix for a flaw that made the whole verb dead on arrival,
 * and it is worth writing down because the reasoning that produced the flaw was
 * superficially good. Steering used to be scaled by the crown's control in the
 * node being steered, on the argument that "steering is what you do with a node
 * you have won, not a way to reach into one you have not". That argument is
 * self-defeating: a node you have won is a node you should be *collecting* in,
 * and the node whose traffic you actually want to redirect is by definition one
 * somebody else holds. Both halves of the mechanic — the pull on the road and
 * the drain on retention — scaled to nothing exactly where the verb was supposed
 * to be used. Measured over forty years of campaign, a steer was worth about
 * 0.3 gold a month against 15 for a collector, and no crown ever chose one.
 *
 * A merchant is a man with carts and money who is standing in the market whether
 * the crown behind him owns the ground or not. He brings this much regardless,
 * and control is added on top — so a crown that *does* hold the node steers it
 * harder, but a crown that holds none of it can still reach in and pull.
 *
 * That makes a steerer into a genuinely hostile act: one merchant can drag most
 * of a rival's market out from under whoever is collecting there. It costs one
 * of a handful of merchants, it needs a node adjacent to ground you hold, and it
 * only pays if you hold the place the value ends up — which is three real
 * constraints, and is what keeps it from being free.
 *
 * **Why it is set this high.** Steering is structurally a second-order way to
 * earn: a collector takes `value × retention × control` straight off the top,
 * while a steerer only ever captures a *fraction of the fraction that leaves*,
 * discounted again by his control downstream. With `RETAIN_FLOOR` at 0.6 only
 * four coins in ten ever leave a node at all, so a weak steerer is fighting over
 * a small pie against a collector eating the whole one — measured at 0.35, a
 * steer was still worth a quarter of a collector and the council still never
 * chose one. At 0.6 a steerer strips a node down to about a fifth of what it
 * would have kept, and funnelling a rival's hinterland into a terminal you
 * dominate finally beats collecting where you stand. That is the EU4 fantasy the
 * owner asked for, and this is the number that buys it.
 *
 * The victim is not helpless, which is what keeps this fair: posting a collector
 * of their own pushes retention back up through `collectShare`, and taking
 * control of the node cuts the steerer's downstream take directly.
 */
export const STEER_MERCHANT_WEIGHT = 0.6;

/**
 * How hard one steering merchant leans, all told. Control on top of his own
 * weight, capped — one rule, used by the flow and by the council alike.
 */
export function steerWeightOf(control: number): number {
  return Math.min(1, STEER_MERCHANT_WEIGHT + Math.max(0, control));
}

/**
 * What a node keeps before anybody collects, given how much of it is being
 * steered.
 *
 * A steerer's control works directly against the floor: half a node steering is
 * a node that keeps half as much of its own trade as it otherwise would, and
 * pushes the difference down the road. That is what makes `Steer` a real
 * alternative to `Collect` rather than a rounding error — and what makes a
 * merchant sent into a market a rival is collecting in an act of war rather than
 * a polite gesture.
 */
export function retainFloorWith(steerShare: number): number {
  return RETAIN_FLOOR * (1 - Math.max(0, Math.min(1, steerShare)));
}

/** The node's retention, from the two shares. One rule, one implementation. */
export function retentionOf(collectShare: number, steerShare: number): number {
  const floor = retainFloorWith(steerShare);
  return floor + (1 - floor) * Math.max(0, Math.min(1, collectShare));
}

// ── Trade power: what control is actually made of ──────────────────────────
//
// Control is a share of total power in the node. Every term below is something
// the player can look at on the map and change with an order.

/** Power per unit of local trade value a crown's own provinces put in. The bread. */
export const POWER_PER_VALUE = 1;
/** Power a merchant is worth, collecting. Compare: a good province is worth 2 to 4. */
export const POWER_PER_MERCHANT = 9;
/** A steering merchant is present but not buying, so it counts for less. */
export const STEER_POWER_SHARE = 0.55;
/** Power per level of fortification held in the node — a garrison the road pays. */
export const POWER_PER_FORT = 2.5;
/** Power per point of army standing on ground in the node. */
export const POWER_PER_ARMY_POINT = 0.006;
/**
 * The most force alone may be worth in one node.
 *
 * Uncapped, a single doomstack parked on a market outweighs every province in it
 * and trade becomes a second scoreboard for whoever has the biggest army —
 * which is the one thing it must not be, because that scoreboard already exists.
 * Force projection should be able to *tip* a contested node, not own one.
 */
export const MAX_ARMY_POWER = 20;
/**
 * Power per level of port held in the node.
 *
 * **The `mapsea` seam.** Nothing writes ports yet; `portLevel` in `trade.ts`
 * reads an optional `port` off `ProvinceState` and returns 0 today. When ports
 * land, coastal control starts answering to them without a line of this file
 * changing. See also `SEA_LANE_WEIGHT`.
 */
export const POWER_PER_PORT = 8;

/**
 * How far control moves toward what the board says it should be, each month.
 *
 * Control is smoothed rather than recomputed outright, which is what makes it
 * *state* rather than a readout of the province table. Taking a market town does
 * not hand you its trade the same month; it takes a season or two for the
 * factors to change hands, and in the meantime the previous holder is still
 * being paid. That lag is the whole reason control is a thing to compete *for*
 * rather than a thing that is simply true.
 */
export const CONTROL_DRIFT = 0.34;

// ── Merchants ──────────────────────────────────────────────────────────────

/**
 * The floor, the earn rate, and the ceiling.
 *
 * A crown on the opening board holds four or five provinces, so it opens with
 * two merchants for sixteen nodes. That is the number the design is about: two
 * is enough to have a policy and nowhere near enough to have a network, so the
 * question "where does the second one go" is a real one on turn one and stays
 * real. Six is the cap because a crown holding forty provinces has already won
 * the argument and does not also need to own every market on the board.
 */
export const MERCHANTS_BASE = 2;
export const MERCHANTS_PER_PROVINCES = 7;
export const MERCHANTS_MAX = 6;

/** Months on the road: one, plus one per this many province hops, capped. */
export const MERCHANT_HOPS_PER_MONTH = 4;
export const MERCHANT_TRAVEL_MAX = 4;

// ── The node graph ─────────────────────────────────────────────────────────

/** Provinces per node, roughly. 127 provinces makes sixteen markets. */
export const NODE_PROVINCES = 8;
/** However big the realm gets, the graph stays legible. */
export const MIN_NODES = 3;
export const MAX_NODES = 40;

/**
 * How much of the flow each kind of road between two markets carries.
 *
 * Relative, and normalised per node, so only the ratios matter. A pass is a bad
 * place to move goods and a good place to rob them; an old road is the best
 * thing the land has.
 *
 * **`SEA_LANE_WEIGHT` is the second `mapsea` seam.** The node graph's edges are
 * built from `CampaignState.links` whatever kind they are, so the day a sea lane
 * becomes a province link it becomes a trade road too, with no change here. Any
 * link kind above `LinkKind.Pass` is assumed to be water and gets this weight,
 * which is deliberately the largest on the table: the whole reason a node graph
 * points at the sea is that the sea is where bulk goods actually move.
 */
export const EDGE_WEIGHT: Record<number, number> = {
  0: 1, // Wild — open country
  1: 1.5, // Road
  2: 1.25, // River — a ford for an army, a barge for a merchant
  3: 0.6, // Pass
};
export const SEA_LANE_WEIGHT = 2.2;

export function edgeWeight(linkKind: number): number {
  return EDGE_WEIGHT[linkKind] ?? SEA_LANE_WEIGHT;
}

/**
 * Is this link water?
 *
 * Anything the land table does not name. `mapsea` owns the link kinds and has not
 * landed its sea lanes yet, so this is written to be true of whatever it adds
 * rather than of a number agreed in advance — the same defensive shape
 * `edgeWeight` has, and for the same reason.
 *
 * It matters because water is the one kind of road that may run between two
 * markets the *same* distance from the sea. See `wireArrows`.
 */
export function isWaterLink(linkKind: number): boolean {
  return EDGE_WEIGHT[linkKind] === undefined;
}

export function createTradeNodeState(nations: number): TradeNodeState {
  return {
    local: 0,
    value: 0,
    retained: 0,
    collectShare: 0,
    steerShare: 0,
    control: new Array<number>(nations).fill(0),
    income: new Array<number>(nations).fill(0),
    outflow: [],
  };
}
